/** Tech Tickets: staff raise requests, the tech team works them. */
import axios, { type AxiosInstance } from "axios";
import { getSessionToken } from "@/lib/session";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

function makeClient(): AxiosInstance {
  const c = axios.create({ baseURL: `${BASE_URL}/api/v1`, timeout: 60_000 });
  c.interceptors.request.use((config) => {
    const token = getSessionToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  return c;
}
const client = makeClient();

export type TicketStatus = "request" | "accepted" | "working" | "rfi" | "completed" | "backburner" | "rejected";
export type DeadlineKind = "none" | "hard" | "soft";

export interface TechTicket {
  id: string; number: number; createdAt: string; updatedAt: string;
  createdById: string; createdByName: string; createdByRole: string;
  software: string; softwareLabel: string; requestType: "feature" | "bug";
  deadlineKind: DeadlineKind; deadline: string | null; description: string;
  status: TicketStatus; statusReason: string | null; backburnerUntil: string | null;
  statusChangedAt: string | null; statusChangedBy: string | null; commentCount: number;
  attachmentCount?: number;
}
export interface TicketComment {
  id: string; createdAt: string; authorName: string; authorRole: string; body: string; isSystem: boolean;
}
export interface TicketAttachment {
  id: string; filename: string | null; contentType: string; sizeBytes: number;
  addedById: string | null; addedByName: string | null; createdAt: string; url: string | null;
}
export interface TechTicketDetail extends TechTicket { comments: TicketComment[]; attachments: TicketAttachment[] }

export const techTicketsApi = {
  list: () => client.get<{ tickets: TechTicket[]; canManage: boolean; software: Record<string, string> }>("/tech-tickets")
    .then((r) => r.data),
  get: (id: string) => client.get<TechTicketDetail>(`/tech-tickets/${id}`).then((r) => r.data),
  create: (body: { software: string; requestType: string; deadlineKind: DeadlineKind; deadline?: string; description: string }) =>
    client.post<TechTicketDetail>("/tech-tickets", body).then((r) => r.data),
  comment: (id: string, body: string) =>
    client.post<TechTicketDetail>(`/tech-tickets/${id}/comments`, { body }).then((r) => r.data),
  setStatus: (id: string, status: TicketStatus, reason?: string, backburnerUntil?: string) =>
    client.post<TechTicketDetail>(`/tech-tickets/${id}/status`, { status, reason, backburnerUntil }).then((r) => r.data),
  remove: (id: string) => client.delete(`/tech-tickets/${id}`).then((r) => r.data),
  /** One image per call (screenshots). */
  attach: (id: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file, file.name || "screenshot.png");
    return client.post<TechTicketDetail>(`/tech-tickets/${id}/attachments`, fd, { timeout: 120_000 }).then((r) => r.data);
  },
  detach: (id: string, attachmentId: string) =>
    client.delete<TechTicketDetail>(`/tech-tickets/${id}/attachments/${attachmentId}`).then((r) => r.data),
};
